/**
 * Position icons (Geben / Hören / Sagen) — the body trio, anchored on the
 * bidding ceremony the way the table teaches it: the dealer gives, forehand
 * hears the first bid, middlehand says it. One grammar across the three:
 * organic outline strokes (one continuous contour plus one inner detail) and
 * sound/motion arcs — the mouth emits them, the ear receives them, the giving
 * hand carries them with a double meaning ("Geben, Hören, Sagen, Weitersagen"
 * — after the first two have spoken, the dealer bids too). Rationale:
 * docs/components/ICONOGRAPHY.md § Position Icons.
 *
 * All factories draw with currentColor so each host surface recolors them
 * (homes per docs/components/ICONOGRAPHY.md § Position Icons).
 */
import { SVG_NS, svg, path } from "./svg.js";

/** Geben — traced giving hand: one continuous contour around thumb,
 *  fingertips and webbing, tilted toward the receiver, with emission arcs
 *  (the give — and the dealer's own later bid; see module header). */
export function createPositionHand(size) {
  const el = svg(size, size);
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("transform", "rotate(12, 24, 30)");
  // One contour: wrist → thumb bump → index → middle → ring → pinky → wrist
  g.appendChild(
    path(
      "M18 42.5 L17.2 34 Q16.8 31.5 13.5 29.2 Q8.2 25.6 9.4 22.8 Q10.6 20.2 13.8 22.8 Q15.4 24.2 15.6 26.5 L15.6 14.2 Q15.6 11.6 17.8 11.6 Q20 11.6 20 14.2 L20 23 L20.5 10.8 Q20.5 8.2 22.8 8.2 Q25.1 8.2 25.1 10.8 L25.1 22.8 L25.5 12.4 Q25.5 9.9 27.8 9.9 Q30.1 9.9 30.1 12.4 L30.1 23.4 L30.5 17 Q30.5 14.6 32.5 14.6 Q34.5 14.6 34.5 17 L34.5 35 Q34.5 42.5 28 42.5 Z",
      2.2,
    ),
  );
  // Palm crease — the one inner detail, like the ear's curl and mouth's bow
  g.appendChild(path("M19.5 36.5 Q24.5 34 30 36.5", 1.4));
  el.appendChild(g);
  // Giving arcs (see double meaning in the module header)
  el.appendChild(path("M39 22 Q42 28 39 34", 2));
  el.appendChild(path("M43.5 18 Q48 28 43.5 38", 2));
  return el;
}

/** Hören — ear with incoming sound arcs on its left: the trio grammar is
 *  organ + arcs (hand gives, mouth emits, ear receives). All arcs bulge in
 *  the direction of travel, so in a row the sound flows Sagen → Hören. */
export function createPositionEar(size) {
  const el = svg(size, size);
  // Outer helix: over the top, down the right, into the lobe
  el.appendChild(
    path(
      "M18 14 Q22 5 29 8 Q36 12 34 21 Q33 27 29 31 Q26 34 25 37 Q24 42 19.5 40.5 Q16 39 17 34",
    ),
  );
  // Inner antihelix curl
  el.appendChild(path("M25 14 Q30 17 28 23 Q27 26 24 25", 2));
  // Incoming sound arcs, arriving from the left
  el.appendChild(path("M9.5 19 Q12.5 25 9.5 31", 2));
  el.appendChild(path("M4.5 15 Q9 25 4.5 35", 2));
  return el;
}

/** Sagen — speaking mouth with sound arcs. */
export function createPositionMouth(size) {
  const el = svg(size, size);
  // Upper lip with cupid's bow (mouth sits left of center; arcs need room)
  el.appendChild(path("M8 23 Q12 18 16.5 20 Q19 21.5 21.5 20 Q26 18 30 23"));
  // Lower lip
  el.appendChild(path("M8 23 Q19 33 30 23"));
  // Sound arcs to the right
  el.appendChild(path("M35 17 Q39 23 35 29", 2));
  el.appendChild(path("M40 13 Q46 23 40 33", 2));
  return el;
}

/** Position icons: the dealer GIVES (hand), forehand HEARS the first bid (ear),
 *  middlehand SAYS it (mouth) — ICONOGRAPHY § Position Icons. Moved here from
 *  render/position-indicator.js (round-inspector-show-dont-tell): the round
 *  inspector became this map's second numeric-position consumer, and
 *  position-indicator.js is a board-render module that drags the animation/etch
 *  graph — importing from it would pull board code onto the /games history page. */
export const POSITION_ICONS = {
  DEALER: createPositionHand,
  FOREHAND: createPositionEar,
  MIDDLEHAND: createPositionMouth,
};
